import React, { useMemo } from 'react';
import { usePlanStore } from '../store/usePlanStore';
import { calculateVolumeMetrics } from '../logic/volumeEngine';
import { calculateFatigueMetrics } from '../logic/fatigueSystem';
import { calculateQualityMetrics } from '../logic/qualityScore';
import VolumeChart from './VolumeChart';
import MuscleHeatmap from './MuscleHeatmap';
import FatigueMeter from './FatigueMeter';
import QualityScoreCard from './QualityScoreCard';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

export default function AnalyticsDashboard() {
  const { plan, activeWeekIndex } = usePlanStore();

  const analytics = useMemo(() => {
    if (!plan) return null;
    const week = plan.weeks[activeWeekIndex];
    if (!week) return null;

    const volume = calculateVolumeMetrics(week, plan.trainingLevel);
    const fatigue = calculateFatigueMetrics(week, plan.fatigueTolerance);
    const quality = calculateQualityMetrics(
      week,
      volume,
      fatigue,
      plan.progressionModel,
      plan.trainingLevel,
      plan.mesocycleLength
    );

    return { volume, fatigue, quality, week };
  }, [plan, activeWeekIndex]);

  if (!plan || !analytics) {
    return (
      <div className="text-center py-16 text-slate-400">
        <p className="text-sm">Utwórz najpierw program, aby zobaczyć analitykę.</p>
      </div>
    );
  }

  const { volume, fatigue, quality } = analytics;

  // Intensity distribution pie data
  const intData = [
    { name: 'Niska (≤RPE 6)', value: volume.intensityDistribution.low, color: '#22c55e' },
    { name: 'Umiarkowana (RPE 7–7.5)', value: volume.intensityDistribution.moderate, color: '#3b82f6' },
    { name: 'Wysoka (RPE 8–8.5)', value: volume.intensityDistribution.high, color: '#f59e0b' },
    { name: 'Maks (RPE ≥9)', value: volume.intensityDistribution.max, color: '#ef4444' },
  ].filter((d) => d.value > 0);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* ── Week selector info ───────────── */}
      <div className="flex items-center gap-4">
        <h2 className="text-lg font-bold">
          Analityka: Tydzień {plan.weeks[activeWeekIndex]?.weekNumber || 1}
        </h2>
        <div className="flex items-center gap-1">
          {plan.weeks.map((w, idx) => (
            <button
              key={w.id}
              onClick={() => usePlanStore.getState().setActiveWeek(idx)}
              className={`w-7 h-7 rounded text-xs font-bold transition ${idx === activeWeekIndex
                  ? 'bg-brand-600 text-white'
                  : 'bg-slate-100 dark:bg-slate-800 text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700'
                }`}
            >
              {w.weekNumber}
            </button>
          ))}
        </div>
      </div>

      {/* ── Quick stats ──────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          label="Łącznie serii"
          value={volume.totalSets}
          subtitle={`${volume.compoundSets} wielostawowych · ${volume.isolationSets} izolowanych`}
        />
        <StatCard
          label="Objętość obciążenia"
          value={volume.volumeLoadScore}
          subtitle="Serie × Powt × Intensywność"
        />
        <StatCard
          label="Indeks zmęczenia"
          value={fatigue.overallFatigue}
          subtitle={`Regeneracja: ${fatigue.recoveryIndex}%`}
          valueColor={fatigue.overallFatigue > 70 ? '#ef4444' : fatigue.overallFatigue > 50 ? '#f59e0b' : '#22c55e'}
        />
        <StatCard
          label="Ocena jakości"
          value={quality.overallScore}
          subtitle={`${quality.suggestions.length} ulepszeń`}
          valueColor={quality.overallScore >= 70 ? '#22c55e' : quality.overallScore >= 50 ? '#f59e0b' : '#ef4444'}
        />
      </div>

      {/* ── Main grid ────────────────────── */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <VolumeChart volume={volume} />
        <MuscleHeatmap volume={volume} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <FatigueMeter fatigue={fatigue} />

        <div className="space-y-6">
          <QualityScoreCard quality={quality} />

          {/* Intensity distribution mini chart */}
          {intData.length > 0 && (
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
              <h3 className="text-sm font-bold mb-1">Rozkład intensywności</h3>
              <p className="text-[10px] text-slate-400 mb-3">
                Podział serii według strefy RPE
              </p>
              <div className="flex items-center gap-4">
                <div className="w-[120px] h-[120px]">
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={intData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={30}
                        outerRadius={50}
                        paddingAngle={2}
                      >
                        {intData.map((d, i) => (
                          <Cell key={i} fill={d.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(val: number) => `${val} serii`}
                        contentStyle={{
                          fontSize: '11px',
                          borderRadius: '8px',
                          border: '1px solid #e2e8f0',
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-1.5 flex-1">
                  {intData.map((d) => (
                    <div key={d.name} className="flex items-center gap-2 text-xs">
                      <div
                        className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
                        style={{ backgroundColor: d.color }}
                      />
                      <span className="text-slate-500 flex-1">{d.name}</span>
                      <span className="font-mono font-bold">{d.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Stat card ───────────────────────────────

function StatCard({
  label,
  value,
  subtitle,
  valueColor,
}: {
  label: string;
  value: number;
  subtitle: string;
  valueColor?: string;
}) {
  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
      <span className="text-[10px] font-medium text-slate-400 uppercase">{label}</span>
      <div className="text-xl font-bold mt-0.5" style={valueColor ? { color: valueColor } : {}}>
        {value}
      </div>
      <span className="text-[10px] text-slate-400">{subtitle}</span>
    </div>
  );
}
